<template>
  <nav class="bottom-tab-bar" role="tablist">
    <router-link
      v-for="tab in tabs"
      :key="tab.route"
      :to="tab.route"
      v-ripple
      class="tab-item"
      :class="{ active: isActive(tab.route) }"
      role="tab"
      :aria-selected="isActive(tab.route)"
      @click="impactLight"
    >
      <span class="tab-icon">
        <font-awesome-icon :icon="tab.icon" />
      </span>
      <span class="tab-label">{{ tab.label }}</span>
    </router-link>
    <!-- Active learning language: first-class tab that opens the switcher sheet -->
    <button
      v-ripple
      class="tab-item language-tab"
      :class="{ active: showLanguageSheet }"
      role="tab"
      :aria-selected="showLanguageSheet"
      data-test="language-tab"
      :aria-label="$t('languageSwitcher.switchTo')"
      @click="openLanguageSheet"
    >
      <span class="tab-icon">
        <img :src="activeFlagSrc" :alt="activeAltText" class="tab-flag" />
      </span>
      <span class="tab-label">{{ languageName(displayedLanguage) }}</span>
    </button>
    <button
      v-ripple
      class="tab-item"
      :class="{ active: showMoreMenu }"
      role="tab"
      @click="toggleMoreMenu"
    >
      <span class="tab-icon">
        <font-awesome-icon icon="ellipsis" />
      </span>
      <span class="tab-label">{{ $t('navigation.more') }}</span>
    </button>

    <!-- More menu popup -->
    <div v-if="showMoreMenu" class="more-menu-overlay" @click="showMoreMenu = false"></div>
    <div v-if="showMoreMenu" class="more-menu">
      <router-link :to="Routes.DASHBOARD_ACCOUNT" class="more-menu-item" @click="showMoreMenu = false">
        <font-awesome-icon icon="user" class="more-menu-icon" />
        <span>{{ $t('navigation.account') }}</span>
      </router-link>
      <button class="more-menu-item" @click="handleSignOut">
        <font-awesome-icon icon="fa-solid fa-sign-out-alt" class="more-menu-icon" />
        <span>{{ $t('navigation.signOut') }}</span>
      </button>
    </div>

    <BottomSheet v-model="showLanguageSheet" :title="$t('languageSwitcher.title')">
      <LanguageSheetContent
        :held-languages="heldLanguages"
        :displayed-language="displayedLanguage"
        @pick="onSheetPick"
        @add="onSheetAdd"
      />
    </BottomSheet>
  </nav>
</template>

<script>
import { ref as vueRef, computed } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { Routes } from "@/constants/routes.js";
import { authUtils } from "@/scripts/authUtils.js";
import { useHaptics } from "@/composables/useHaptics.js";
import { useLearningLanguages } from "@/composables/useLearningLanguages.js";
import { featureFlags } from "@/store/featureFlags.js";
import BottomSheet from "./BottomSheet.vue";
import LanguageSheetContent from "./LanguageSheetContent.vue";

export default {
  name: "BottomTabBar",
  components: { BottomSheet, LanguageSheetContent },
  emits: ["add-language", "upgrade-required"],
  setup(_props, { emit }) {
    const route = useRoute();
    const { t } = useI18n();
    const { impactLight } = useHaptics();
    const {
      heldLanguages,
      displayedLanguage,
      isMultiLingual,
      activeFlagSrc,
      activeAltText,
      languageName,
      switchTo,
    } = useLearningLanguages();
    const showMoreMenu = vueRef(false);
    const showLanguageSheet = vueRef(false);

    // Today goes FIRST when the flag is on, so the bar reads Today, Course,
    // Chats, the language switcher, More. It stays a computed rather than
    // becoming a static list because featureFlags starts every flag false and
    // flips it moments later when its RTDB listeners answer; a list built once
    // in setup would leave an allowlisted learner without the tab until the
    // next remount of the bar.
    const tabs = computed(() => [
      ...(featureFlags.dailyPlay
        ? [
          {
            route: Routes.DASHBOARD_TODAY,
            icon: "calendar-check",
            label: t("navigation.today"),
          },
        ]
        : []),
      { route: Routes.DASHBOARD_COURSE, icon: "book", label: t("navigation.myCourse") },
      { route: Routes.DASHBOARD_CHAT, icon: "comments", label: t("navigation.myChats") },
    ]);

    const isActive = (tabRoute) => {
      return route.path.startsWith(tabRoute);
    };

    const toggleMoreMenu = () => {
      impactLight();
      showMoreMenu.value = !showMoreMenu.value;
    };

    const openLanguageSheet = () => {
      impactLight();
      showLanguageSheet.value = true;
    };

    const onSheetPick = (code) => {
      impactLight();
      showLanguageSheet.value = false;
      switchTo(code);
    };

    // DashboardView hosts the add-language / upgrade modals.
    const onSheetAdd = () => {
      showLanguageSheet.value = false;
      emit(isMultiLingual.value ? "add-language" : "upgrade-required");
    };

    const handleSignOut = async () => {
      showMoreMenu.value = false;
      await authUtils.signOut();
    };

    return {
      tabs,
      isActive,
      impactLight,
      showMoreMenu,
      toggleMoreMenu,
      handleSignOut,
      heldLanguages,
      displayedLanguage,
      activeFlagSrc,
      activeAltText,
      languageName,
      showLanguageSheet,
      openLanguageSheet,
      onSheetPick,
      onSheetAdd,
      Routes,
    };
  },
};
</script>

<style scoped>
.bottom-tab-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: calc(56px + var(--al-tab-bar-inset));
  padding-bottom: var(--al-tab-bar-inset);
  background: var(--al-surface-raised);
  border-top: 1px solid var(--al-border);
  display: none;
  justify-content: space-around;
  align-items: center;
  z-index: 1000;
  isolation: isolate;
}

@media (max-width: 768px) {
  .bottom-tab-bar {
    display: flex;
  }
}

.tab-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 6px 12px;
  color: var(--al-text-muted);
  font-size: 10px;
  font-family: inherit;
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;
  background: none;
  border: none;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  min-width: 64px;
  transition: color 0.15s ease;
}

.tab-item.active {
  color: var(--al-primary);
}

.tab-icon {
  font-size: 22px;
  line-height: 1;
}

.tab-flag {
  display: block;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
}

.language-tab .tab-label {
  max-width: 72px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tab-label {
  font-size: 10px;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
}

/* More menu */
.more-menu-overlay {
  position: fixed;
  inset: 0;
  z-index: 1;
}

.more-menu {
  position: absolute;
  bottom: calc(100% + 8px);
  right: 8px;
  background: var(--al-surface-raised);
  border-radius: var(--al-radius-lg);
  box-shadow: var(--al-shadow-md);
  padding: 4px 0;
  min-width: 160px;
  z-index: 2;
}

.more-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 16px;
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  color: var(--al-text);
  text-decoration: none;
  transition: background-color 0.15s ease;
}

.more-menu-item:active {
  background-color: var(--al-surface-sunken);
}

.more-menu-icon {
  font-size: 16px;
  width: 20px;
  text-align: center;
}
</style>
